import React, { useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch } from '../api.js';
import Button from '../ui/Button.jsx';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/Card.jsx';
import {
  HouseholdContextFields,
  PersonalContextFields
} from './HouseholdProfileForm.jsx';
import {
  buildProfilePayload,
  findInvalidProfileField,
  getInitialProfileForm,
  getMissingProfileFields,
  HOUSEHOLD_CONTEXT_FIELDS,
  PERSONAL_CONTEXT_FIELDS
} from '../utils/profileForm.js';
import { scrollElementToTop } from '../utils/scroll.js';

const STEPS = [
  {
    id: 'personal',
    badge: 'Étape 1 sur 2',
    title: 'Commençons par vous',
    description: 'Ces questions décrivent votre rythme de vie personnel et servent à contextualiser le questionnaire.',
    fields: PERSONAL_CONTEXT_FIELDS
  },
  {
    id: 'household',
    badge: 'Étape 2 sur 2',
    title: 'Puis votre foyer',
    description: 'Ces questions décrivent le foyer lui-même. Les données statistiques facultatives viendront plus tard.',
    fields: HOUSEHOLD_CONTEXT_FIELDS
  }
];

function StepIndicator({ currentStep }) {
  return (
    <div className="flex items-center gap-2">
      {STEPS.map((step, index) => {
        const isActive = index === currentStep;
        const isDone = index < currentStep;

        return (
          <div
            key={step.id}
            className={[
              'h-2.5 flex-1 rounded-full transition-colors',
              isDone || isActive ? 'bg-ptcm-primary' : 'bg-ptcm-panel dark:bg-gray-800'
            ].join(' ')}
          />
        );
      })}
    </div>
  );
}

export default function ProfileSetupFlow({ token, member, onCompleted, embedded = false }) {
  const topRef = useRef(null);
  const initial = useMemo(() => getInitialProfileForm(member), [member]);
  const [form, setForm] = useState(initial);
  const [currentStep, setCurrentStep] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setForm(initial);
  }, [initial]);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      scrollElementToTop(topRef.current);
    });

    return () => window.cancelAnimationFrame(frame);
  }, [currentStep]);

  const step = STEPS[currentStep];

  const setField = (key, value) => {
    setForm((previous) => ({ ...previous, [key]: value }));
  };

  const goNext = () => {
    const missingFields = getMissingProfileFields(form, step.fields);
    if (missingFields.length > 0) {
      setError('Veuillez répondre aux questions obligatoires de cette étape avant de continuer.');
      return;
    }

    setError('');
    setCurrentStep((value) => Math.min(value + 1, STEPS.length - 1));
  };

  const saveProfile = async () => {
    const missingFields = getMissingProfileFields(form);
    if (missingFields.length > 0) {
      setError('Certaines réponses obligatoires sont encore manquantes.');
      return;
    }

    const payload = buildProfilePayload(form);
    const invalidField = findInvalidProfileField(form, payload);
    if (invalidField) {
      setError('Certaines valeurs dépassent les limites attendues.');
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      await apiFetch('/api/family/profile', { token, method: 'POST', body: payload });
      await onCompleted?.();
    } catch (err) {
      setError(err?.message || 'Impossible d’enregistrer vos informations.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      ref={topRef}
      className={embedded ? 'max-w-3xl mx-auto px-3 sm:px-4 py-4' : 'max-w-3xl mx-auto mt-6 px-3 sm:px-4 pb-10'}
    >
      <Card className="overflow-hidden">
        <CardHeader className="space-y-4">
          <div className="ptcm-section-pill w-fit">
            Parcours guidé
          </div>
          <div className="space-y-2">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-ptcm-muted dark:text-gray-400">
              {step.badge}
            </div>
            <CardTitle className="text-2xl leading-tight">{step.title}</CardTitle>
            <CardDescription className="text-base">{step.description}</CardDescription>
          </div>
          <StepIndicator currentStep={currentStep} />
        </CardHeader>

        <CardContent className="space-y-6">
          {error ? (
            <div className="rounded-[24px] border border-red-200/70 dark:border-red-900/30 bg-red-50/90 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-300">
              {error}
            </div>
          ) : null}

          {currentStep === 0 ? (
            <div className="space-y-4">
              <div className="rounded-[28px] border border-ptcm-border dark:border-gray-800 bg-ptcm-panel/60 dark:bg-gray-950/30 p-4 sm:p-5">
                <PersonalContextFields form={form} setField={setField} />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-[28px] border border-ptcm-border dark:border-gray-800 bg-ptcm-panel/60 dark:bg-gray-950/30 p-4 sm:p-5">
                <HouseholdContextFields form={form} setField={setField} />
              </div>
            </div>
          )}

          <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-3">
            {currentStep === 0 ? <div /> : (
              <Button variant="outline" size="lg" onClick={() => setCurrentStep((value) => Math.max(value - 1, 0))}>
                Retour
              </Button>
            )}

            {currentStep === 0 ? (
              <Button size="lg" onClick={goNext}>
                Continuer
              </Button>
            ) : (
              <Button size="lg" onClick={saveProfile} disabled={isSaving}>
                {isSaving ? 'Enregistrement…' : 'Commencer le questionnaire'}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
